import Head from 'next/head';
import { useRouter } from 'next/router';
import type { FunctionComponent } from 'react';

import { getPlatform, isB2B } from '@/core/settings/utils';
import { getPlatformCurrency } from '@/domains/core/localization/utils/getPlatformCurrency';
import { getCurrentBaseUrl } from '@/domains/core/platform/getCurrentBaseUrl';
import {
  ENERGY_ATTRIBUTE_ID,
  MAX_ENERGY_ATTRIBUTE_ID,
  MIN_ENERGY_ATTRIBUTE_ID,
} from '@/domains/productDiscovery/ProductPage/common/components/AddToCart/IndexLabels/useEnergy';
import { useDetail } from '@/domains/productDiscovery/ProductPage/hooks/useDetail';
import { useBrand } from '@/domains/productDiscovery/ProductPage/modules/Brand/useBrand';
import { ProductSecondHandContext } from '@/domains/sustainability/secondHand/utils/secondHand';

import type {
  BreadcrumbSchema,
  ProductSchema,
} from './interfaces/MicroData.model';
import { metaDescription } from './translations';

const LENGTH_ATTRIBUTE_ID = 1;
const WIDTH_ATTRIBUTE_ID = 2;
const HEIGHT_ATTRIBUTE_ID = 3;
const WEIGHT_ATTRIBUTE_ID = 7;
const SIZE_ATTRIBUTE_ID = 43;
const MATERIAL_ATTRIBUTE_ID = 32;
const COLOR_ATTRIBUTE_ID = 34;
const MAX_CHARACTER_NUMBER_MATERIAL = 200;
const MAX_CHARACTER_NUMBER_COLOR = 100;

export const SEO: FunctionComponent = () => {
  const baseUrl = getCurrentBaseUrl();
  const {
    breadcrumbs = [],
    ean,
    manufacturerSKU,
    media = [],
    price,
    articlePath,
    title,
    averageRating,
    totalRating,
    latestReviews,
    isSellable,
    description,
    attributes,
    secondHandDetails,
    totalModels,
    modelId,
  } = useDetail();
  const brand = useBrand();
  const { asPath } = useRouter();

  const rawDescription =
    description
      ?.replace(/(<([^>]+)>)/gi, '')
      .replace(/ *(\r\n|\n|\r|\\n)/g, '')
      .trim()
      .toString() ?? '';

  const metaDescriptionFormatted = metaDescription
    .format({
      title: title || '',
      sku: manufacturerSKU || '',
    })
    .toString();

  const canonical =
    getPlatform() === 'es' && totalModels > 1
      ? `${baseUrl}/p/${articlePath}?model_id=${modelId}`
      : `${baseUrl}/p/${articlePath}`;

  const category = breadcrumbs[breadcrumbs.length - 1]?.name || '';

  const priceCurrency = getPlatformCurrency(getPlatform());
  const seoPrice = isB2B()
    ? price?.primaryPrice?.amountWithoutVat
    : price?.primaryPrice?.amountWithVat;

  const maxEnergyAttribute = attributes?.find(
    (attribute) => attribute.id === MAX_ENERGY_ATTRIBUTE_ID,
  );
  const minEnergyAttribute = attributes?.find(
    (attribute) => attribute.id === MIN_ENERGY_ATTRIBUTE_ID,
  );
  const energyAttribute = attributes?.find(
    (attribute) => attribute.id === ENERGY_ATTRIBUTE_ID,
  );
  const lengthAttribute = attributes?.find(
    (attribute) => attribute.id === LENGTH_ATTRIBUTE_ID,
  );
  const widthAttribute = attributes?.find(
    (attribute) => attribute.id === WIDTH_ATTRIBUTE_ID,
  );
  const heightAttribute = attributes?.find(
    (attribute) => attribute.id === HEIGHT_ATTRIBUTE_ID,
  );
  const weightAttribute = attributes?.find(
    (attribute) => attribute.id === WEIGHT_ATTRIBUTE_ID,
  );
  const sizeAttribute = attributes?.find(
    (attribute) => attribute.id === SIZE_ATTRIBUTE_ID,
  );
  const materialAttribute = attributes?.find(
    (attribute) => attribute.id === MATERIAL_ATTRIBUTE_ID,
  );
  const colorAttribute = attributes?.find(
    (attribute) => attribute.id === COLOR_ATTRIBUTE_ID,
  )?.value;

  const getItemCondition = () => {
    const context = secondHandDetails?.context;

    if (
      context === ProductSecondHandContext.REFURBISHED ||
      context === ProductSecondHandContext.NEVER_USED
    ) {
      return 'https://schema.org/RefurbishedCondition';
    }
    if (context === ProductSecondHandContext.USED) {
      return 'https://schema.org/UsedCondition';
    }
    return 'https://schema.org/NewCondition';
  };

  const productSchema: ProductSchema = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: title,
    description: rawDescription,
    image: media.map(({ regularUrl: imageUrl }) => imageUrl || ''),
    sku: `${manufacturerSKU}`,
    model: `${manufacturerSKU}`,
    brand: { '@type': 'Brand', name: brand.name },
    manufacturer: { '@type': 'Organization', name: brand.name },
    gtin: ean,
    category,
    url: canonical,
    review: [],
    ...(colorAttribute &&
      colorAttribute.length < MAX_CHARACTER_NUMBER_COLOR && {
        color: colorAttribute,
      }),
    ...(weightAttribute && {
      weight: `${weightAttribute.value} ${weightAttribute.unit}`,
    }),
    ...(sizeAttribute && {
      size: sizeAttribute.value,
    }),
    ...(materialAttribute &&
      materialAttribute.value.length < MAX_CHARACTER_NUMBER_MATERIAL && {
        material: materialAttribute.value,
      }),
  };

  productSchema.offers = {
    '@type': 'Offer',
    url: canonical,
    priceCurrency,
    price: isSellable ? `${seoPrice}` : '0',
    itemCondition: getItemCondition(),
    availability: isSellable
      ? 'https://schema.org/InStock'
      : 'https://schema.org/OutOfStock',
  };

  if (totalRating > 0) {
    productSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: averageRating,
      reviewCount: totalRating,
      bestRating: 5,
      worstRating: 1,
    };
  }

  const reviews = latestReviews?.bestRatedReviews
    ? latestReviews.bestRatedReviews
    : latestReviews?.latestReviews || [];
  productSchema.review = reviews.map(({ rating, customerName, message }) => ({
    '@type': 'Review',
    reviewRating: { '@type': 'Rating', ratingValue: rating },
    author: { '@type': 'Person', name: customerName },
    reviewBody: message,
  }));

  const breacrumbSchema: BreadcrumbSchema = {};

  if (breadcrumbs) {
    breacrumbSchema['@context'] = 'https://schema.org/';
    breacrumbSchema['@type'] = 'BreadcrumbList';
    breacrumbSchema.itemListElement = breadcrumbs.map(({ name, url }, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name,
      item: `${baseUrl}${url}`,
    }));
  }

  if (energyAttribute && minEnergyAttribute && maxEnergyAttribute) {
    productSchema.hasEnergyConsumptionDetails = {
      '@type': 'EnergyConsumptionDetails',
      hasEnergyEfficiencyCategory: `https://schema.org/EUEnergyEfficiencyCategory${energyAttribute}`,
      energyEfficiencyScaleMin: `https://schema.org/EUEnergyEfficiencyCategory${minEnergyAttribute}`,
      energyEfficiencyScaleMax: `https://schema.org/EUEnergyEfficiencyCategory${maxEnergyAttribute}`,
    };
  }

  if (lengthAttribute && widthAttribute && heightAttribute) {
    productSchema.hasMeasurement = {
      '@type': 'QuantitativeValue',
      width: {
        '@type': 'QuantitativeValue',
        value: widthAttribute?.value,
        unitText: widthAttribute?.unit,
      },
      length: {
        '@type': 'QuantitativeValue',
        value: lengthAttribute?.value,
        unitText: lengthAttribute?.unit,
      },
      height: {
        '@type': 'QuantitativeValue',
        value: heightAttribute?.value,
        unitText: heightAttribute?.unit,
      },
    };
  }

  return (
    <>
      <Head>
        {title && <title key="meta_title">{title}</title>}
        <meta
          name="description"
          content={metaDescriptionFormatted}
          key="meta_description"
        />
        {asPath.includes('/catalogue/p') && (
          <meta
            name="robots"
            content="noindex, follow"
            key="pp-catalogue-noindex"
          />
        )}
        {canonical && (
          <meta property="og:url" content={canonical} key="pp-og-url" />
        )}
        <meta property="og:type" content="Product" key="pp-og-type" />
        <meta property="og:title" content={title} key="pp-og-title" />
        <meta
          property="og:description"
          content={metaDescriptionFormatted}
          key="pp-og-description"
        />
        <meta
          property="og:image"
          content={media[0]?.regularUrl as string}
          key="pp-og-image"
        />
        <meta name="twitter:url" content={canonical} key="pp-twitter-url" />
        <meta name="twitter:title" content={title} key="pp-twitter-title" />
        <meta
          name="twitter:description"
          content={metaDescriptionFormatted}
          key="pp-twitter-description"
        />
        <meta
          name="twitter:image"
          content={media[0]?.regularUrl as string}
          key="pp-twitter-image"
        />
        <meta name="twitter:card" content="summary" key="twitter:card" />
        <link rel="canonical" href={canonical} key="pp-canonical" />
        <script
          type="application/ld+json"
          key="pp-product-microdata"
          data-testid="structuredDataSEOProductElm"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(productSchema),
          }}
        />
        <script
          type="application/ld+json"
          key="pp-breadcrumb-microdata"
          data-testid="structuredDataSEOBreadcrumbElm"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(breacrumbSchema),
          }}
        />
      </Head>
    </>
  );
};

SEO.displayName = 'SEO';
